import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Field, PageHeader, Panel, RowsSkeleton } from "@/components/app/kit";
import { FieldInput } from "@/components/app/FormDialog";
import { useFetch } from "@/hooks/useServerList";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const KEYS = ["platform_name", "base_domain", "routing_target", "support_email", "default_country", "default_language", "default_trial_days", "password_min_length", "maintenance_mode"];

export default function PlatformSettings() {
  const { me } = useAuth();
  const isSuper = me.user.platform_role === "super_admin";
  const { data } = useFetch("/platform/settings");
  const ref = useFetch("/platform/reference");
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (data) setV(data);
  }, [data]);
  if (!v || !ref.data) return <RowsSkeleton rows={6} />;
  const fields = [
    { key: "platform_name", label: "Nom de la plateforme" },
    { key: "support_email", label: "E-mail de support", type: "email" },
    { key: "base_domain", label: "Domaine principal", mono: true, hint: "Les sous-domaines des cabinets sont créés sous ce domaine (valeur provisoire à remplacer)" },
    { key: "routing_target", label: "Cible CNAME de routage", mono: true, hint: "Hôte vers lequel les domaines des cabinets doivent pointer" },
    { key: "default_country", label: "Pays par défaut", type: "select", options: ref.data.countries.map((c) => [c.code, c.name]) },
    { key: "default_language", label: "Langue par défaut", type: "select", options: ref.data.languages.filter((l) => l.available).map((l) => [l.code, l.name]) },
    { key: "default_trial_days", label: "Durée d'essai par défaut (jours)", type: "number" },
    { key: "password_min_length", label: "Longueur minimale des mots de passe", type: "number" },
    { key: "maintenance_mode", label: "Mode maintenance", type: "switch" },
  ].map((f) => ({ ...f, disabled: !isSuper }));
  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(KEYS.map((k) => [k, v[k]]));
      body.default_trial_days = Number(body.default_trial_days);
      body.password_min_length = Number(body.password_min_length);
      body.support_email = body.support_email || null;
      setV((await api.put("/platform/settings", body)).data);
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="max-w-4xl">
      <PageHeader eyebrow="Configuration" title="Paramètres généraux de la plateforme" subtitle={isSuper ? undefined : "Lecture seule : seuls les super administrateurs peuvent modifier ces paramètres."} />
      <Panel>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {fields.map((f) => (
            <Field key={f.key} label={f.label} hint={f.hint}><FieldInput field={f} value={v[f.key]} onChange={(x) => setV((s) => ({ ...s, [f.key]: x }))} testPrefix="settings" /></Field>
          ))}
        </div>
        {isSuper && <div className="mt-8 pt-5 border-t border-slate-100"><Button onClick={save} disabled={busy} data-testid="settings-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Enregistrer</Button></div>}
      </Panel>
    </div>
  );
}
